import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { LayoutDashboard, Route as RouteIcon, Receipt, FileText, Settings, LogOut } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { SubscriptionProvider } from "@/context/SubscriptionContext";
import { LegalFooter } from "@/components/LegalFooter";

export const NAV = [
  { to: "/dashboard", label: "Dashboard", short: "Home", icon: LayoutDashboard, id: "nav-dashboard-link" },
  { to: "/mileage", label: "Mileage Log", short: "Mileage", icon: RouteIcon, id: "nav-mileage-log-link" },
  { to: "/expenses", label: "Expenses", short: "Expenses", icon: Receipt, id: "nav-expenses-link" },
  { to: "/tax-report", label: "Tax Report", short: "Report", icon: FileText, id: "nav-tax-report-link" },
  { to: "/settings", label: "Settings", short: "Settings", icon: Settings, id: "nav-settings-link" },
];

const DesktopNav = () => (
  <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
    {NAV.map(({ to, label, icon: Icon, id }) => (
      <NavLink
        key={to}
        to={to}
        data-testid={id}
        className={({ isActive }) =>
          `flex items-center gap-2 px-3.5 py-2 rounded-lg text-sm font-semibold transition-colors ${
            isActive ? "bg-emerald-50 text-emerald-900" : "text-slate-600 hover:text-slate-900 hover:bg-slate-100"
          }`
        }
      >
        <Icon className="w-4 h-4" />
        {label}
      </NavLink>
    ))}
  </nav>
);

const MobileNav = () => (
  <nav className="mt-bottom-nav md:hidden" data-testid="mobile-nav">
    {NAV.map(({ to, short, icon: Icon, id }) => (
      <NavLink
        key={to}
        to={to}
        data-testid={`mobile-${id}`}
        className={({ isActive }) =>
          `flex flex-col items-center justify-center gap-1 flex-1 h-full text-[11px] font-semibold transition-colors ${
            isActive ? "text-emerald-800" : "text-slate-500"
          }`
        }
      >
        {({ isActive }) => (
          <>
            <span className={`flex items-center justify-center w-12 h-7 rounded-full transition-colors ${isActive ? "bg-emerald-100" : ""}`}>
              <Icon className="w-5 h-5" />
            </span>
            {short}
          </>
        )}
      </NavLink>
    ))}
  </nav>
);

export default function AppLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const doLogout = async () => {
    await logout();
    navigate("/login");
  };
  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      <header className="sticky top-0 z-40 backdrop-blur-md bg-white/90 border-b border-slate-200/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 h-16 flex items-center justify-between gap-4">
          <Logo />
          <DesktopNav />
          <div className="flex items-center gap-3">
            <span className="hidden lg:block text-sm text-slate-500 max-w-[180px] truncate" data-testid="header-user-email">
              {user?.email}
            </span>
            <button
              onClick={doLogout}
              data-testid="logout-button"
              className="flex items-center gap-2 h-10 px-3 rounded-lg text-sm font-semibold text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors"
            >
              <LogOut className="w-4 h-4" />
              <span className="hidden sm:inline">Log out</span>
            </button>
          </div>
        </div>
      </header>
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10 pb-28 md:pb-16">
        <SubscriptionProvider>
          <Outlet />
        </SubscriptionProvider>
        <LegalFooter className="mt-12 pt-6 border-t border-slate-200" />
      </main>
      <MobileNav />
    </div>
  );
}
